import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Lock, Mail, ArrowRight, AlertCircle, Loader2, Phone, CheckCircle2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const AuthModal = () => {
  const {
    isAuthModalOpen, closeAuthModal, authView, setAuthView,
    login, register, loading, error, completeSession, forgotPassword, resetPassword
  } = useAuth();

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    password_confirmation: ''
  });

  // Estado para el flujo de verificación dentro del modal
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [verifyMessage, setVerifyMessage] = useState(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Estado para recuperación y restablecimiento de contraseña
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [recoverError, setRecoverError] = useState(null);
  const [resetData, setResetData] = useState({
    code: '',
    password: '',
    password_confirmation: ''
  });

  // Lógica del Carrusel de Imágenes
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = [
    '/Carrusel_Ejemplo.png',
    '/Carrusel_Ejemplo_2.png',
    '/Carrusel_Ejemplo_3.png'
  ];

  useEffect(() => {
    if (isAuthModalOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      }, 3500);
      return () => {
        clearInterval(interval);
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    }
  }, [isAuthModalOpen, images.length]);

  // Temporizador para reenviar código en la vista 'verify'
  useEffect(() => {
    let interval = null;
    if (authView === 'verify' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [authView, resendTimer]);

  // Limpiar automáticamente todos los mensajes y alertas al cambiar de vista
  useEffect(() => {
    setVerifyError(null);
    setVerifyMessage(null);
    setRecoverError(null);
  }, [authView]);

  const handleViewChange = (view) => {
    setAuthView(view);
    setFormData({ name: '', email: '', password: '', password_confirmation: '', phone: '' });
    setOtpCode(['', '', '', '', '', '']);
    setVerifyError(null);
    setVerifyMessage(null);
    setRecoverError(null);
    setResetData({ code: '', password: '', password_confirmation: '' });
  };

  const handleRecoverSubmit = async (e) => {
    e.preventDefault();
    if (recoverLoading) return;
    setRecoverLoading(true);
    setRecoverError(null);

    const res = await forgotPassword(formData.email);
    setRecoverLoading(false);

    if (res.success) {
      setAuthView('reset_password');
      setResetData({ code: '', password: '', password_confirmation: '' });
      setVerifyMessage('Código de recuperación enviado a tu correo. Por favor ingresa el código y tu nueva contraseña.');
      setVerifyError(null);
    } else {
      setRecoverError(res.message || 'No se pudo enviar el código de recuperación.');
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (verifyLoading) return;
    setVerifyLoading(true);
    setVerifyError(null);
    setVerifyMessage(null);

    if (resetData.password !== resetData.password_confirmation) {
      setVerifyError('Las contraseñas no coinciden.');
      setVerifyLoading(false);
      return;
    }

    const res = await resetPassword({
      email: formData.email,
      code: resetData.code,
      password: resetData.password,
      password_confirmation: resetData.password_confirmation
    });
    setVerifyLoading(false);

    if (res.success) {
      setAuthView('login');
      alert('¡Contraseña restablecida exitosamente! Por favor inicia sesión con tu nueva contraseña.');
    } else {
      setVerifyError(res.message || 'Error al restablecer la contraseña.');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const result = await login({ email: formData.email, password: formData.password });

    if (result?.requiresVerification) {
      // Si la cuenta no está verificada, llevar inmediatamente al usuario a la vista de código OTP
      setAuthView('verify');
      setResendTimer(60);
      setCanResend(false);
      setOtpCode(['', '', '', '', '', '']);
      setVerifyError(result.message || 'Tu correo electrónico no ha sido verificado. Por favor ingresa el código enviado a tu correo.');
      setVerifyMessage(null);
      return;
    }

    if (result.success) {
      const role = result.user?.role?.toLowerCase().trim();
      closeAuthModal();

      setTimeout(() => {
        if (role !== 'cliente') {
          navigate('/admin/dashboard');
        } else {
          navigate('/');
        }
      }, 100);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const result = await register(formData);
    if (result?.success) {
      // Cambiar la vista del modal a 'verify' para ingresar el código OTP
      setAuthView('verify');
      setResendTimer(60);
      setCanResend(false);
      setOtpCode(['', '', '', '', '', '']);
      setVerifyError(null);
      setVerifyMessage(null);
    }
  };

  const handleOtpChange = (index, value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length > 1) {
      const newCode = ['', '', '', '', '', ''];
      const pastedDigits = cleanValue.slice(0, 6);
      for (let i = 0; i < pastedDigits.length; i++) {
        newCode[i] = pastedDigits[i];
      }
      setOtpCode(newCode);
      const nextIndex = Math.min(pastedDigits.length, 5);
      const nextInput = document.getElementById(`modal-otp-${nextIndex}`);
      if (nextInput) nextInput.focus();
      return;
    }

    if (isNaN(value)) return;
    const newCode = [...otpCode];
    newCode[index] = value.slice(-1);
    setOtpCode(newCode);

    if (value && index < 5) {
      const nextInput = document.getElementById(`modal-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;

    const newCode = ['', '', '', '', '', ''];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setOtpCode(newCode);

    const nextIndex = Math.min(pastedData.length, 5);
    const targetInput = document.getElementById(`modal-otp-${nextIndex}`);
    if (targetInput) targetInput.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`modal-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    const codeStr = otpCode.join('');
    if (codeStr.length !== 6) {
      setVerifyError('Por favor ingresa los 6 dígitos del código.');
      return;
    }
    setVerifyLoading(true);
    setVerifyError(null);
    setVerifyMessage(null);

    try {
      const response = await api.post('/verify-email', {
        email: formData.email,
        code: codeStr
      });

      setVerifyMessage('¡Correo verificado con éxito! Iniciando sesión...');

      const resToken = response.data?.token;
      const resUser = response.data?.user;

      if (resToken && resUser) {
        // Autologin completo y actualización del estado global
        completeSession(resToken, resUser);
        setAuthView('login');
        const role = resUser?.role?.toLowerCase().trim();
        setTimeout(() => {
          if (role !== 'cliente') {
            navigate('/admin/dashboard');
          } else {
            navigate('/');
          }
        }, 600);
      } else {
        setTimeout(() => {
          setAuthView('login');
          closeAuthModal();
          navigate('/');
        }, 1000);
      }
    } catch (err) {
      setVerifyError(err.response?.data?.message || 'Código incorrecto o expirado.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || !formData.email) return;
    setVerifyError(null);
    setVerifyMessage(null);
    try {
      await api.post('/resend-verification', { email: formData.email });
      setVerifyMessage('Nuevo código enviado a tu correo.');
      setResendTimer(60);
      setCanResend(false);
    } catch (err) {
      setVerifyError(err.response?.data?.message || 'Error al reenviar el código.');
    }
  };

  if (!isAuthModalOpen) return null;

  // Renderizado dinámico según la vista activa
  const renderForm = () => {
    const currentView = authView || 'login';

    if (currentView === 'login') {
      return (
        <motion.div
          key="login"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <h2 className="text-3xl lg:text-2xl font-black text-black dark:text-white mb-3" style={{ fontFamily: "'Secular One', sans-serif" }}>Bienvenido de nuevo</h2>
          <p className="text-base lg:text-sm text-gray-500 dark:text-white/60 mb-8">Ingresa tus datos para acceder a tu panel.</p>

          {error && (
            <div className="mb-6 flex items-center gap-2 bg-red-100 text-red-700 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-red-200">
              <AlertCircle size={22} className="lg:w-[18px] lg:h-[18px]" /> {error}
            </div>
          )}

          <form onSubmit={handleLoginSubmit} className="space-y-5 lg:space-y-3">
            <div className="relative">
              <Mail className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="email" required placeholder="Correo Electrónico"
                value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] transition-all shadow-inner text-xl lg:text-base"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="password" required placeholder="Contraseña"
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] transition-all shadow-inner text-xl lg:text-base"
              />
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={() => handleViewChange('recover')} className="text-sm lg:text-xs font-bold text-[#CE010B] dark:text-white/80 hover:underline">¿Olvidaste tu contraseña?</button>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#CE010B] disabled:opacity-70 hover:bg-red-800 dark:bg-gray-900 dark:hover:bg-black dark:border-2 dark:border-white text-white font-bold py-5 lg:py-3 rounded-2xl lg:rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-xl lg:text-base">
              {loading ? 'Procesando...' : 'Iniciar Sesión'} {!loading && <ArrowRight size={24} className="lg:w-[18px] lg:h-[18px]" />}
            </button>

            <p className="text-center text-base lg:text-sm text-gray-600 dark:text-white/60 mt-6">
              ¿No tienes cuenta? <button type="button" onClick={() => handleViewChange('register')} className="font-bold text-[#CE010B] dark:text-white hover:underline">Regístrate aquí</button>
            </p>
          </form>
        </motion.div>
      );
    }

    if (currentView === 'register') {
      return (
        <motion.div
          key="register"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <h2 className="text-3xl lg:text-2xl font-black text-black dark:text-white mb-3" style={{ fontFamily: "'Secular One', sans-serif" }}>Crear Cuenta</h2>
          <p className="text-base lg:text-sm text-gray-500 dark:text-white/60 mb-8">Únete a Más Envíos para gestionar tus paquetes.</p>

          {error && (
            <div className="mb-6 flex items-center gap-2 bg-red-100 text-red-700 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-red-200">
              <AlertCircle size={22} className="lg:w-[18px] lg:h-[18px]" /> {error}
            </div>
          )}

          <form onSubmit={handleRegisterSubmit} className="space-y-5 lg:space-y-3">
            <div className="relative">
              <User className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="text"
                required
                placeholder="Nombre Completo"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z\sÁÉÍÓÚáéíóúÑñ]/g, '');
                  setFormData({ ...formData, name: value });
                }}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="email" required placeholder="Correo Electrónico"
                value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="tel"
                placeholder="Teléfono (10 dígitos)"
                value={formData.phone}
                onChange={(e) => setFormData({
                  ...formData,
                  phone: e.target.value.replace(/\D/g, '').slice(0, 10)
                })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="password" required placeholder="Contraseña" minLength="8"
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="password" required placeholder="Confirmar Contraseña" minLength="8"
                value={formData.password_confirmation} onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>

            <button type="submit" disabled={loading} className="w-full bg-[#CE010B] disabled:opacity-70 hover:bg-red-800 dark:bg-gray-900 dark:hover:bg-black dark:border-2 dark:border-white text-white font-bold py-5 lg:py-3 rounded-2xl lg:rounded-xl shadow-lg transition-all text-xl lg:text-base">
              {loading ? 'Registrando...' : 'Registrarme'}
            </button>

            <p className="text-center text-base lg:text-sm text-gray-600 dark:text-white/60 mt-6">
              ¿Ya tienes cuenta? <button type="button" onClick={() => handleViewChange('login')} className="font-bold text-[#CE010B] dark:text-white hover:underline">Inicia Sesión</button>
            </p>
          </form>
        </motion.div>
      );
    }

    if (currentView === 'verify') {
      return (
        <motion.div
          key="verify"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <h2 className="text-3xl lg:text-2xl font-black text-black dark:text-white mb-2" style={{ fontFamily: "'Secular One', sans-serif" }}>Verificar Correo</h2>
          <p className="text-base lg:text-sm text-gray-600 dark:text-white/70 mb-6">
            Ingresa el código de 6 dígitos enviado a:<br />
            <strong className="text-[#CE010B] dark:text-white font-semibold">{formData.email}</strong>
          </p>

          {verifyError && (
            <div className="mb-6 flex items-center gap-2 bg-red-100 text-red-700 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-red-200">
              <AlertCircle size={22} className="lg:w-[18px] lg:h-[18px]" /> {verifyError}
            </div>
          )}

          {verifyMessage && (
            <div className="mb-6 flex items-center gap-2 bg-emerald-100 text-emerald-800 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-emerald-200">
              <CheckCircle2 size={22} className="lg:w-[18px] lg:h-[18px]" /> {verifyMessage}
            </div>
          )}

          <form onSubmit={handleVerifySubmit} className="space-y-6">
            {/* 6 Inputs OTP */}
            <div className="flex justify-center gap-2">
              {otpCode.map((digit, index) => (
                <input
                  key={index}
                  id={`modal-otp-${index}`}
                  type="text"
                  maxLength={index === 0 ? "6" : "1"}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  onPaste={handleOtpPaste}
                  className="w-11 h-14 bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 text-center text-xl font-bold text-black dark:text-white rounded-xl outline-none focus:border-[#CE010B] transition-all shadow-inner"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={verifyLoading}
              className="w-full bg-[#CE010B] disabled:opacity-70 hover:bg-red-800 dark:bg-gray-900 dark:hover:bg-black dark:border-2 dark:border-white text-white font-bold py-5 lg:py-3 rounded-2xl lg:rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-xl lg:text-base"
            >
              {verifyLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Verificando...</span>
                </>
              ) : (
                <>
                  <span>Confirmar Código</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          {/* Reenviar código */}
          <div className="mt-6 pt-4 border-t border-black/10 dark:border-white/10 text-center text-sm text-gray-600 dark:text-white/70">
            ¿No recibiste el código?{' '}
            {canResend ? (
              <button
                type="button"
                onClick={handleResendOtp}
                className="font-bold text-[#CE010B] dark:text-white hover:underline bg-transparent border-none cursor-pointer"
              >
                Reenviar código
              </button>
            ) : (
              <span className="font-mono text-gray-400 dark:text-white/40">Reenviar en {resendTimer}s</span>
            )}
          </div>

          <p className="text-center text-sm mt-4">
            <button type="button" onClick={() => handleViewChange('login')} className="font-bold text-gray-500 dark:text-white/60 hover:underline">
              ← Volver al inicio de sesión
            </button>
          </p>
        </motion.div>
      );
    }

    if (currentView === 'recover') {
      return (
        <motion.div
          key="recover"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <h2 className="text-3xl lg:text-2xl font-black text-black dark:text-white mb-3" style={{ fontFamily: "'Secular One', sans-serif" }}>Recuperar Acceso</h2>
          <p className="text-base lg:text-sm text-gray-500 dark:text-white/60 mb-6">Ingresa tu correo registrado y te enviaremos un código de recuperación de 6 dígitos.</p>

          {recoverError && (
            <div className="mb-6 flex items-center gap-2 bg-red-100 text-red-700 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-red-200">
              <AlertCircle size={22} className="lg:w-[18px] lg:h-[18px]" /> {recoverError}
            </div>
          )}

          <form onSubmit={handleRecoverSubmit} className="space-y-5 lg:space-y-3">
            <div className="relative">
              <Mail className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="email" required placeholder="Correo Electrónico"
                value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full pl-14 pr-6 py-5 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>

            <button type="submit" disabled={recoverLoading} className="w-full bg-[#CE010B] disabled:opacity-70 hover:bg-red-800 dark:bg-gray-900 dark:hover:bg-black dark:border-2 dark:border-white text-white font-bold py-5 lg:py-3 rounded-2xl lg:rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-xl lg:text-base">
              {recoverLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Enviando Código...</span>
                </>
              ) : (
                <>
                  <span>Enviar Código de Recuperación</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <p className="text-center text-base lg:text-sm mt-6">
              <button type="button" onClick={() => handleViewChange('login')} className="font-bold text-[#CE010B] dark:text-white/80 hover:underline">Volver al Login</button>
            </p>
          </form>
        </motion.div>
      );
    }

    if (currentView === 'reset_password') {
      return (
        <motion.div
          key="reset_password"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <h2 className="text-3xl lg:text-2xl font-black text-black dark:text-white mb-2" style={{ fontFamily: "'Secular One', sans-serif" }}>Restablecer Contraseña</h2>
          <p className="text-base lg:text-sm text-gray-600 dark:text-white/70 mb-6">
            Ingresa el código de 6 dígitos enviado a <strong className="text-[#CE010B]">{formData.email}</strong> y define tu nueva contraseña.
          </p>

          {verifyError && (
            <div className="mb-6 flex items-center gap-2 bg-red-100 text-red-700 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-red-200">
              <AlertCircle size={22} className="lg:w-[18px] lg:h-[18px]" /> {verifyError}
            </div>
          )}

          {verifyMessage && (
            <div className="mb-6 flex items-center gap-2 bg-emerald-100 text-emerald-800 p-4 lg:p-3 rounded-2xl lg:rounded-xl text-base lg:text-sm font-bold border border-emerald-200">
              <CheckCircle2 size={22} className="lg:w-[18px] lg:h-[18px]" /> {verifyMessage}
            </div>
          )}

          <form onSubmit={handleResetSubmit} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="text" required placeholder="Código de 6 dígitos (OTP)" maxLength="6"
                value={resetData.code} onChange={(e) => setResetData({ ...resetData, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                className="w-full pl-14 pr-6 py-4 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base tracking-widest font-mono font-bold"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="password" required placeholder="Nueva Contraseña" minLength="8"
                value={resetData.password} onChange={(e) => setResetData({ ...resetData, password: e.target.value })}
                className="w-full pl-14 pr-6 py-4 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-5 lg:left-4 top-1/2 -translate-y-1/2 text-gray-400 size={24} lg:w-[18px] lg:h-[18px]" />
              <input
                type="password" required placeholder="Confirmar Nueva Contraseña" minLength="8"
                value={resetData.password_confirmation} onChange={(e) => setResetData({ ...resetData, password_confirmation: e.target.value })}
                className="w-full pl-14 pr-6 py-4 lg:pl-11 lg:pr-4 lg:py-3 rounded-2xl lg:rounded-xl bg-white dark:bg-black/20 border border-black/10 dark:border-white/20 dark:text-white focus:outline-none focus:border-[#CE010B] shadow-inner text-xl lg:text-base"
              />
            </div>

            <button
              type="submit" disabled={verifyLoading}
              className="w-full bg-[#CE010B] disabled:opacity-70 hover:bg-red-800 dark:bg-gray-900 dark:hover:bg-black dark:border-2 dark:border-white text-white font-bold py-4 lg:py-3 rounded-2xl lg:rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 text-xl lg:text-base"
            >
              {verifyLoading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Actualizando...</span>
                </>
              ) : (
                <>
                  <span>Guardar Nueva Contraseña</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <p className="text-center text-sm mt-4">
              <button type="button" onClick={() => handleViewChange('login')} className="font-bold text-gray-500 dark:text-white/60 hover:underline">
                ← Volver al inicio de sesión
              </button>
            </p>
          </form>
        </motion.div>
      );
    }

    return null;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm overflow-y-auto p-4 flex items-center justify-center"
        onClick={closeAuthModal}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[95%] md:max-w-[680px] lg:max-w-4xl bg-white dark:bg-neutral-900 rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col lg:flex-row my-8 lg:my-0 relative border dark:border-[3px] dark:border-white/20"
        >
          <button onClick={closeAuthModal} className="absolute top-4 right-4 z-50 p-2 bg-white/50 dark:bg-black/50 hover:bg-gray-200 dark:hover:bg-white/20 rounded-full transition-colors backdrop-blur-md">
            <X size={20} className="text-black dark:text-white" />
          </button>

          {/* MITAD IZQUIERDA: Carrusel de Imágenes */}
          <div className="hidden lg:block lg:w-1/2 relative bg-neutral-800 overflow-hidden lg:h-auto order-2 lg:order-1 flex-shrink-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentImageIndex}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url('${images[currentImageIndex]}')`,
                  backgroundColor: '#590000'
                }}
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex-col justify-end p-6 lg:p-10 hidden lg:flex">
              <h3 className="text-white text-2xl lg:text-3xl font-black leading-tight" style={{ fontFamily: "'Secular One', sans-serif" }}>
                Gestión logística<br />al siguiente nivel.
              </h3>
              <p className="text-white/80 mt-2 font-medium text-sm lg:text-base">Cotiza, envía y rastrea en tiempo real.</p>
            </div>
          </div>

          {/* MITAD DERECHA: Formularios */}
          <div className="w-full lg:w-1/2 p-10 lg:p-12 flex flex-col justify-center transition-colors bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] dark:from-[#590000] dark:to-[#780000] order-1 lg:order-2">
            <div className="w-full max-w-full lg:max-w-none mx-auto flex flex-col justify-center">
              <div className="flex justify-center mb-8 lg:mb-8">
                <img src="/logo_def.png" alt="Grupo TBT" className="h-20 lg:h-14 object-contain block dark:hidden" />
                <img src="/logo_oscuro.png" alt="Grupo TBT" className="h-20 lg:h-14 object-contain hidden dark:block" />
              </div>

              <AnimatePresence mode="wait">
                {renderForm()}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;